import { Badge } from '@/components/admin/shared/badge'
import { Modal } from '@/components/admin/shared/modal'
import {
  getProcessingTypeForAction,
  type ProcessingType,
  type TriggerActionType,
} from './_lib/processing-trigger-policy'

interface PendingChangeItem {
  key: string
  label: string
  previousValue: string | number
  currentValue: string | number
  tabId: string
  triggerActionType: TriggerActionType
}

interface PendingChangesModalProps {
  isOpen: boolean
  pendingChanges: PendingChangeItem[]
  onClose: () => void
}

const PROCESSING_LABEL: Record<ProcessingType, string> = {
  none: 'None',
  process: 'Process',
  reprocess: 'Reprocess',
  full_reprocess: 'Full reprocess',
}

const PROCESSING_BADGE: Record<
  ProcessingType,
  'active' | 'Medium' | 'Hard' | 'default'
> = {
  none: 'default',
  process: 'active',
  reprocess: 'Medium',
  full_reprocess: 'Hard',
}

function formatTabLabel(tabId: string): string {
  return tabId.replaceAll('-', ' ')
}

function renderEntityCell(item: PendingChangeItem) {
  if (!item.key.startsWith('recommendation-status:')) {
    return <p className="text-foreground text-sm font-medium">{item.label}</p>
  }

  const recommendationId = item.key.replace('recommendation-status:', '')
  return (
    <p className="text-foreground text-sm font-medium">
      {item.label} ({recommendationId})
    </p>
  )
}

export function PendingChangesModal({
  isOpen,
  pendingChanges,
  onClose,
}: PendingChangesModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Pending changes"
      size="wide"
    >
      <div className="space-y-3">
        {pendingChanges.length === 0 ? (
          <p className="text-muted-foreground text-sm">No pending changes.</p>
        ) : (
          <>
            <p className="text-muted-foreground text-sm">
              These changes will be included in the next processing run.
            </p>
            <div className="border-border overflow-hidden rounded-md border">
              <div className="bg-muted/40 grid grid-cols-[2fr_1fr_1fr_1fr_1fr] gap-2 px-3 py-2 text-[11px] font-semibold">
                <span>Change</span>
                <span>Previous</span>
                <span>Current</span>
                <span>Processing type</span>
                <span>Tab</span>
              </div>
              {pendingChanges.map((item) => (
                <div
                  key={item.key}
                  className="border-border grid grid-cols-[2fr_1fr_1fr_1fr_1fr] gap-2 border-t px-3 py-2 text-xs"
                >
                  <div>{renderEntityCell(item)}</div>
                  <p className="text-muted-foreground tabular-nums">
                    {String(item.previousValue)}
                  </p>
                  <p className="text-foreground tabular-nums">
                    {String(item.currentValue)}
                  </p>
                  <div>
                    <Badge
                      variant={
                        PROCESSING_BADGE[
                          getProcessingTypeForAction(item.triggerActionType)
                        ]
                      }
                    >
                      {
                        PROCESSING_LABEL[
                          getProcessingTypeForAction(item.triggerActionType)
                        ]
                      }
                    </Badge>
                  </div>
                  <div>
                    <Badge variant="default">
                      {formatTabLabel(item.tabId)}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}
